import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Button, DatePicker, Descriptions, InputNumber, Select, Space, Tag, Typography} from 'antd';
import {PlayerContext} from '../../../../shared/contexts/playerContext';
import {AuthContext} from '../../../../shared/contexts/authContext';
import {useItemKrTextData, useShopPackage, useShopPackageGroup} from '../../../probability/useItemData';
import {getItemInfo} from '../../../../shared/utils/basic';
import IsLoading from '../../../../shared/components/IsLoading';
import ErrorPage from '../../../../shared/components/ErrorPage';
import {internalApi} from '../../../../common/axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const {Text} = Typography;

const MAX_TABS = 3;
const DAYS_PER_TAB = 3;

const emptyTab = () => ({
    GroupIndex: 0,
    PurchaseDate: dayjs.utc().toISOString(),
    DayRewardClaimed: Array(DAYS_PER_TAB).fill(false),
});

const ThreeDayStreak = () => {
    const {playerData, setPlayerData} = useContext(PlayerContext);
    const {currentUser} = useContext(AuthContext);
    const {data: shopPackage, isLoading: isPackageLoading, error: packageError} = useShopPackage();
    const {data: shopPackageGroup, isLoading: isGroupLoading, error: groupError} = useShopPackageGroup();
    const {data: krItemTextData, isLoading: isItemTextLoading, error: itemTextError} = useItemKrTextData();

    const playerInfo = playerData?.playerInfo;
    const streak = playerInfo?.shopData?.ActivePackageThreeDayStreak;

    const [form, setForm] = useState(null);
    const [isModified, setIsModified] = useState(false);
    const [saving, setSaving] = useState(false);
    const [grantGroupIndex, setGrantGroupIndex] = useState(null);

    // streak 가 null 이어도 폼은 항상 존재한다. (null 이면 지급으로 새로 만들 수 있어야 하므로)
    useEffect(() => {
        setForm({
            PackageIndex: streak?.PackageIndex ?? 0,
            Tabs: (streak?.Tabs || []).map(tab => ({
                GroupIndex: tab.GroupIndex ?? 0,
                PurchaseDate: tab.PurchaseDate,
                DayRewardClaimed: Array.from(
                    {length: DAYS_PER_TAB},
                    (_, i) => Boolean(tab.DayRewardClaimed?.[i]),
                ),
            })),
        });
        setIsModified(false);
    }, [streak]);

    // 대표 인덱스(PackageIndex) -> groupIdx -> Shop_Package_Group.csv 의 순서대로인 행 목록(= inapp 아이디 순서)
    const groupRows = useMemo(() => {
        const groupIdx = shopPackage?.[currentUser.target]?.[form?.PackageIndex]?.groupIdx;
        if (groupIdx === undefined) return [];
        return Object.values(shopPackageGroup?.[currentUser.target] || {})
            .filter(group => group.groupIdx === groupIdx)
            .sort((a, b) => a.index - b.index);
    }, [shopPackage, shopPackageGroup, currentUser.target, form?.PackageIndex]);

    // 각 탭의 대표 인덱스 = inAppIdx 를 가진 행. 그 뒤 DAYS_PER_TAB 개 행이 해당 탭의 일차별 보상.
    const tabRows = useMemo(() => groupRows.filter(group => group.inAppIdx), [groupRows]);

    // 1번 탭부터 순차 지급. 지급된 탭 수가 곧 다음에 지급할 탭의 위치가 된다.
    const nextGrantIndex = tabRows[(form?.Tabs || []).length]?.index;

    const groupOptions = useMemo(() => tabRows.map((group, idx) => {
        const granted = (form?.Tabs || []).some(tab => tab.GroupIndex === group.index);
        const suffix = granted ? ' · 지급됨' : (group.index === nextGrantIndex ? '' : ' · 이전 탭 먼저 지급');
        return {
            value: group.index,
            // disabled: group.index !== nextGrantIndex,
            label: `${idx + 1}번 탭 · ${group.index} · inApp ${group.inAppIdx}${group.nameText ? ` · ${group.nameText}` : ''}${suffix}`,
        };
    }), [tabRows, form?.Tabs, nextGrantIndex]);

    // 대표 인덱스부터 DAYS_PER_TAB 개 행 = 해당 탭의 일차별 보상
    const dayRowsOf = (groupIndex) => {
        const start = groupRows.findIndex(group => group.index === groupIndex);
        return start < 0 ? [] : groupRows.slice(start, start + DAYS_PER_TAB);
    };

    const rewardText = (reward) => {
        const name = getItemInfo(krItemTextData?.[currentUser.target] || [], reward.id, reward.type)?.name;
        return `${name || reward.id} x${reward.quantity}`;
    };

    const updateTab = (tabIdx, patch) => {
        setForm(prev => ({
            ...prev,
            Tabs: prev.Tabs.map((tab, i) => (i === tabIdx ? {...tab, ...patch} : tab)),
        }));
        setIsModified(true);
    };

    const handleRemoveTab = (tabIdx) => {
        setForm(prev => ({...prev, Tabs: prev.Tabs.filter((_, i) => i !== tabIdx)}));
        setIsModified(true);
    };

    const persist = async (nextStreak, successMessage, failMessage) => {
        setSaving(true);
        try {
            const nextPlayerInfo = {
                ...playerInfo,
                shopData: {
                    ...(playerInfo?.shopData || {}),
                    ActivePackageThreeDayStreak: {
                        ...(streak || {}),
                        ...nextStreak,
                    },
                },
            };

            await internalApi.post('/player/playInfo', {
                playInfo: nextPlayerInfo,
                userKey: playerData.userKey,
                syncId: playerData.syncId,
            });

            setPlayerData(prev => ({...prev, playerInfo: nextPlayerInfo}));
            setIsModified(false);
            toast.success(successMessage);
        } catch (e) {
            toast.error(e?.response?.data?.message || failMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => persist(
        {PackageIndex: form.PackageIndex, Tabs: form.Tabs},
        '저장되었습니다.',
        '저장 실패',
    );

    const handleGrant = () => {
        const row = tabRows.find(group => group.index === grantGroupIndex);
        if (!row) {
            toast.error('지급할 대표 인덱스를 선택하세요.');
            return;
        }
        if (form.Tabs.length >= MAX_TABS) {
            toast.error(`탭은 최대 ${MAX_TABS}개까지만 지급할 수 있습니다.`);
            return;
        }
        // if (grantGroupIndex !== nextGrantIndex) {
        //     toast.error('1번 탭부터 순차적으로만 지급할 수 있습니다.');
        //     return;
        // }
        if (!window.confirm(`대표 인덱스 ${row.index} (inApp ${row.inAppIdx}) 를 지급하시겠습니까?`)) return;

        const tabs = [...form.Tabs, {...emptyTab(), GroupIndex: grantGroupIndex}];
        setForm(prev => ({...prev, Tabs: tabs}));
        setGrantGroupIndex(null); // 지급하면 다음 탭이 대상이 되므로 선택 초기화
        return persist(
            {PackageIndex: form.PackageIndex, Tabs: tabs},
            '지급되었습니다.',
            '지급 실패',
        );
    };

    if (isPackageLoading || isGroupLoading || isItemTextLoading) return <IsLoading/>;
    const firstError = packageError || groupError || itemTextError;
    if (firstError) return <ErrorPage error={firstError}/>;

    if (!form) return <IsLoading/>;

    return (
        <div style={{maxWidth: 900, margin: '0 auto', padding: 24}}>
            <h2 style={{marginBottom: 20}}>
                3일 연속 패키지{' '}
                {!streak && <Tag color="default">데이터 없음 · 지급 시 생성</Tag>}
            </h2>

            <div style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}>
                <h3 style={{marginBottom: 16}}>패키지 정보</h3>
                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="대표 인덱스 (PackageIndex)">
                        <Space wrap>
                            {streak
                                ? <Text>{form.PackageIndex}</Text>
                                : <InputNumber
                                    value={form.PackageIndex}
                                    onChange={(v) => {
                                        setForm(prev => ({...prev, PackageIndex: v ?? 0}));
                                        setGrantGroupIndex(null); // 패키지가 바뀌면 지급 선택도 무효
                                    }}
                                    min={0}
                                    style={{width: 200}}
                                />}
                            {groupRows.length === 0 && <Tag color="warning">CSV에 없는 인덱스</Tag>}
                        </Space>
                    </Descriptions.Item>
                    <Descriptions.Item label="상품 지급 (대표 인덱스)">
                        <Space wrap>
                            <Select
                                value={grantGroupIndex}
                                onChange={setGrantGroupIndex}
                                options={groupOptions}
                                style={{minWidth: 320}}
                                placeholder="지급할 대표 인덱스 선택"
                                notFoundContent="PackageIndex 를 먼저 입력하세요."
                            />
                            <Button
                                type="primary"
                                onClick={handleGrant}
                                loading={saving}
                                disabled={grantGroupIndex === null || form.Tabs.length >= MAX_TABS}
                            >
                                지급
                            </Button>
                            {form.Tabs.length >= MAX_TABS && <Tag color="warning">탭 {MAX_TABS}개 모두 사용중</Tag>}
                        </Space>
                    </Descriptions.Item>
                </Descriptions>
            </div>

            {form.Tabs.map((tab, tabIdx) => (
                <div
                    key={tabIdx}
                    style={{marginBottom: 24, padding: '16px 20px', background: '#fafafa', border: '1px solid #d9d9d9', borderRadius: 8}}
                >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16}}>
                        <h3 style={{margin: 0}}>
                            {(() => {
                                // 번호는 드롭다운과 같이 CSV 대표 인덱스 순서를 따른다
                                const tabNo = tabRows.findIndex(group => group.index === tab.GroupIndex);
                                return tabNo >= 0 ? `${tabNo + 1}번 탭` : '탭';
                            })()}
                        </h3>
                        <Button danger size="small" onClick={() => handleRemoveTab(tabIdx)}>탭 삭제</Button>
                    </div>
                    <Descriptions bordered size="small" column={1}>
                        <Descriptions.Item label="대표 인덱스 (GroupIndex)">
                            <Space wrap>
                                <Text>{tab.GroupIndex}</Text>
                                {(() => {
                                    const row = tabRows.find(g => g.index === tab.GroupIndex);
                                    return row
                                        ? <Tag color="blue">inApp {row.inAppIdx}</Tag>
                                        : <Tag color="warning">CSV에 없는 대표 인덱스</Tag>;
                                })()}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="구매 일시 (PurchaseDate, UTC)">
                            <DatePicker
                                showTime
                                value={tab.PurchaseDate ? dayjs.utc(tab.PurchaseDate) : null}
                                onChange={(d) => updateTab(tabIdx, {PurchaseDate: d ? d.utc().toISOString() : null})}
                                format="YYYY-MM-DD HH:mm:ss"
                                style={{width: 220}}
                            />
                        </Descriptions.Item>
                        <Descriptions.Item label="일차별 보상 (DayRewardClaimed)">
                            {(() => {
                                const dayRows = dayRowsOf(tab.GroupIndex);
                                return tab.DayRewardClaimed.map((claimed, dayIdx) => {
                                    const row = dayRows[dayIdx];
                                    return (
                                        <div
                                            key={dayIdx}
                                            style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6}}
                                        >
                                            <Tag color={claimed ? 'purple' : 'default'}>
                                                {dayIdx + 1}일차: {claimed ? '수령' : '미수령'}
                                            </Tag>
                                            {row
                                                ? <Text type="secondary">
                                                    {row.rewards?.length
                                                        ? row.rewards.map(rewardText).join(', ')
                                                        : '보상 없음'}
                                                </Text>
                                                : <Text type="secondary">CSV 행 없음</Text>}
                                        </div>
                                    );
                                });
                            })()}
                        </Descriptions.Item>
                    </Descriptions>
                </div>
            ))}

            <div style={{display: 'flex', justifyContent: 'center', gap: 8}}>
                <Button type="primary" onClick={handleSave} loading={saving} disabled={!isModified}>저장</Button>
            </div>
        </div>
    );
};

export default ThreeDayStreak;
